import React, { useCallback, useEffect, useState } from 'react';

import adminMarketPlace from '../../../api/modules/admin.market-place';
import { showToast } from '../../../utils/toast';
import { MarketPlaceDashboardView } from './MarketPlaceDashbord.view';

/**
 * Marketplace listing queue, the container. Owns fetching, the debounced
 * filters, pagination and the approve/reject pair.
 * `MarketPlaceDashbord.view.jsx` holds everything visual and is pure.
 *
 * BEHAVIOUR PRESERVED
 * -------------------
 * Same endpoint, same three filter params (`status`, `card_name`,
 * `store_name`), same 500ms debounce, same reset-to-page-1 on a filter change,
 * same optimistic local update on approve and reject, same toasts, same
 * "do not refetch while the detail panel is open" guard.
 *
 * TWO FIXES IN PASSING
 * --------------------
 * 1. `loading` was initialised true and only ever set false on SUCCESS, so a
 *    failed fetch left the page on a permanent skeleton with no message. There
 *    is now a real error state with a retry.
 * 2. The old filter panel kept its OWN `currentPage` counter alongside the one
 *    the API returned, and the two drifted apart the moment a filter changed.
 *    There is one page number now, derived from the server's pagination block.
 *
 * The two `console.log` calls that printed the whole selected listing and the
 * whole result set on every render are gone.
 */
const PAGE_SIZE = 10;
const EMPTY_FILTERS = { status: '', card_name: '', store_name: '' };

const MarketPlaceDashbord = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE });

  const [selected, setSelected] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  // Typing filters debounce; a Select commits immediately, but sending both
  // down the same path keeps one request shape and one reset rule.
  useEffect(() => {
    const timer = setTimeout(() => setAppliedFilters(filters), 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminMarketPlace.getListings({ ...appliedFilters, page });
      if (!res.success) {
        setError(new Error(res.error || 'Failed to load listings'));
        return;
      }
      setListings(res.data?.results || []);
      setPagination(res.data?.pagination || { total: 0, page, limit: PAGE_SIZE });
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    if (selected) return;
    fetchListings();
  }, [selected, fetchListings]);

  const openListing = (listing) => {
    setSelected(listing);
    setImageIndex(0);
  };

  const imageCount = selected?.images?.length || 0;
  const stepImage = (delta) => {
    if (!imageCount) return;
    setImageIndex((prev) => (prev + delta + imageCount) % imageCount);
  };

  const handleApprove = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const res = await adminMarketPlace.approveListing(selected.id);
      if (res.success) {
        showToast.success('Listing approved successfully');
        setListings((prev) =>
          prev.map((row) => (row.id === selected.id ? { ...row, status: 'approved' } : row)),
        );
        setSelected((prev) => (prev ? { ...prev, status: 'approved' } : prev));
        setConfirmApproveOpen(false);
      } else {
        showToast.error(res.error || 'Failed to approve listing');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async (reason) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const res = await adminMarketPlace.updateListingStatus(selected.id, 'rejected', reason);
      if (res.success) {
        showToast.success('Listing rejected successfully');
        setListings((prev) =>
          prev.map((row) =>
            row.id === selected.id ? { ...row, status: 'rejected', rejection_reason: reason } : row,
          ),
        );
        setSelected((prev) =>
          prev ? { ...prev, status: 'rejected', rejection_reason: reason } : prev,
        );
        setRejectOpen(false);
      } else {
        showToast.error(res.error || 'Failed to reject listing');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const total = pagination?.total || 0;
  const limit = pagination?.limit || PAGE_SIZE;
  const pageCount = pagination?.total_pages || Math.max(1, Math.ceil(total / limit));

  return (
    <MarketPlaceDashboardView
      listings={listings}
      loading={loading}
      error={error}
      onRetry={fetchListings}
      filters={filters}
      onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
      onClearFilters={() => setFilters(EMPTY_FILTERS)}
      page={page}
      pageCount={pageCount}
      pageSize={limit}
      total={total}
      onPageChange={setPage}
      selected={selected}
      onOpen={openListing}
      onClose={() => setSelected(null)}
      imageIndex={imageIndex}
      onPrevImage={() => stepImage(-1)}
      onNextImage={() => stepImage(1)}
      onApprove={handleApprove}
      onReject={handleReject}
      actionBusy={actionBusy}
      confirmApproveOpen={confirmApproveOpen}
      onConfirmApproveOpen={() => setConfirmApproveOpen(true)}
      onConfirmApproveClose={() => setConfirmApproveOpen(false)}
      rejectOpen={rejectOpen}
      onRejectOpen={() => setRejectOpen(true)}
      onRejectClose={() => setRejectOpen(false)}
    />
  );
};

export default MarketPlaceDashbord;
